import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Trash2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { z } from "zod";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import FormInputField from "@/components/lib/from-input-field";
import { toast } from "react-hot-toast";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { ButtonType } from "@/components/lib/Button";
import { getFirstErrorMessage } from "@/lib/utils";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

export default function CreateCourseItem({
  isOpen,
  onClose,
  title,
  onSuccess,
}) {
  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      flatItem: "",
      item_id: null,
      name: "",
      description: "",
      item_type: "",
      variant_id: null,
      max_selections: null,
      file_manager_id: null,
      file_manager_url: "",
      quantity: 1,
      price: null,
    },
  });
  const dispatch = useDispatch();
  const { flatMenus } = useSelector((state) => state.menuManage.menu);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [courseItems, setCourseItems] = useState([]);
  const selectedValue = form.watch("flatItem");
  const variantId = form.watch("variant_id");
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);
  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    if (selectedValue) {
      const matchedItem = flatMenus.find((menu) =>
        selectedValue.includes(menu.unique_id)
      );

      setSelectedItem(matchedItem || null);
      if (matchedItem?.has_variants) {
        const varId = matchedItem.variants[0].id;
        form.setValue("variant_id", varId);
      }
    } else {
      setSelectedItem(null);
      form.setValue("variant_id", null);
    }
  }, [selectedValue, flatMenus]);

  useEffect(() => {
    if (variantId && selectedItem?.has_variants) {
      const foundVariant = selectedItem?.variants?.find(
        (item) => String(item.id) === String(variantId)
      );
      setSelectedVariant(foundVariant);
    } else {
      setSelectedVariant(null);
    }
  }, [variantId, selectedValue, selectedItem]);

  const addItem = async (e) => {
    e.preventDefault();
    const data = form.getValues();
    try {
      const courseItem = {
        unique_id: uuidv4(),
        name: selectedItem.name,
        item_id: selectedItem?.id,
        item_type: selectedItem?.menu_type || "standard_food",
        variant_id: selectedVariant?.id || null,
        variant_name: selectedVariant?.name || null,
        variant: selectedVariant,
        file_manager_id: selectedItem?.file_manager_id || null,
        quantity: data?.quantity || 1,
        price:
          selectedItem?.price ||
          selectedVariant?.in_price ||
          selectedVariant?.out_price ||
          null,
      };
      setCourseItems([...courseItems, courseItem]);
      form.setValue("flatItem", "");
      setSelectedItem(null);
      setSelectedVariant(null);
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong.");
    }
  };

  const onSubmit = (data) => {
    if (courseItems?.length === 0) {
      toast.error("You must add at least 1 item before saving.");
      return;
    }
    try {
      const newCourse = {
        unique_id: uuidv4(),
        name: data.name || "Untitled Course",
        description: `Customer can select max ${
          data.max_selections || 1
        } item At a time`,
        max_selections: Number(data.max_selections) || 1,
        sort_order: 0,
        course_items: courseItems,
      };
      dispatch(setItemVariants([...itemVariants, newCourse]));
      if (onSuccess) {
        onSuccess(newCourse);
      }
      toast.success("Course added successfully!");
      setCourseItems([]);
      form.reset();
      onClose();
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong.");
    }
  };

  const handleRemove = (uniqueId) => {
    setCourseItems((prevItems) =>
      prevItems.filter((item) => item.unique_id !== uniqueId)
    );
    toast.success("Item removed successfully");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            {title || "Create Combo Item"}
          </DialogTitle>
          <Separator className="mt-4" />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6 mt-5"
            >
              <FormInputField
                name="name"
                style="bg-cblack3"
                placeholder="Name"
                form={form}
                label="Course Name"
                type="text"
              />
              <div>
                <FormInputField
                  name="flatItem"
                  style="bg-cblack3"
                  placeholder="Select Item name"
                  form={form}
                  selectOption={
                    flatMenus && flatMenus.length > 0
                      ? flatMenus.map((item) => ({
                          label: item.name,
                          value: item.unique_id,
                        }))
                      : [
                          {
                            label: "No option available",
                            value: "",
                            disabled: true,
                          },
                        ]
                  }
                  label="Select Item"
                  search={true}
                  type="select"
                />
              </div>
              {selectedItem && selectedItem.has_variants === true && (
                <FormInputField
                  name="variant_id"
                  style="bg-cblack3"
                  placeholder="Select variant"
                  form={form}
                  selectOption={
                    selectedItem?.variants && selectedItem.variants.length > 0
                      ? selectedItem.variants.map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))
                      : [
                          {
                            label: "No option available",
                            value: "",
                            disabled: true,
                          },
                        ]
                  }
                  label="Select Variant"
                  type="select"
                />
              )}

              {selectedItem !== null && (
                <ButtonType onClick={addItem} className={"w-40"}>
                  Add Item
                </ButtonType>
              )}

              {courseItems.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {courseItems &&
                      courseItems.length > 0 &&
                      courseItems.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            {item?.name ||
                              item?.menu_item?.name ||
                              "Data not available"}
                            {(item?.variant_name || item?.variant?.name) && ` (${item?.variant_name || item?.variant?.name})`}
                          </TableCell>

                          <TableCell>{item.price ?? "N/A"}</TableCell>
                          <TableCell className="text-right">
                            <span className="flex items-center justify-end gap-3">
                              <Trash2
                                onClick={() => handleRemove(item?.unique_id)}
                                className="w-4 h-4 text-cred cursor-pointer"
                              />
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
              <FormInputField
                name="max_selections"
                style="bg-cblack3"
                placeholder="1"
                form={form}
                label="Customer Can Select"
                type="text"
              />

              <Button type="submit" className="bg-cyellow w-full text-cblack">
                Confirm
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const schema = z.object({
  item_type: z.string().optional(),
  item_name: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  file_manager_url: z.string().optional(),
  variant_id: z.number().nullable().optional(),
  file_manager_id: z.number().nullable().optional(),
  max_selections: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === "") return undefined;
      return Number(val);
    },
    z
      .number({
        invalid_type_error: "Selections must be a number",
      })
      .min(1, "Selections must be at least 1")
  ),
});
